/**
 * shell/RightBar.tsx —— 座位 `rightbar`（壳规范 §5：**push（默认）／fullscreen**）。
 *
 * 规范逐字：**无标题行**；两条控件（形态切换·折叠）搭在 **tab 条末端**；宽度手柄**仅普通展开态**显示；
 *         底色字号取**会话区底色与正文字号**（页面的一列，不是浮卡）；**折叠时零代价**（无轨条、无宽度）；
 *         入口＝**会话 header 的展开钮**（★A-035：header **末位**＝右上角）；**无会话时**无按钮、无面板。
 * 移动端（§1bis）：右栏＝**真全屏**（声明 `phone.rightbar_forms:[fullscreen]`；★A-036）——
 *         几何落 `.xg-rightbar[data-form="fullscreen"]`（`position:fixed; inset:0`；手机档另加 `height:100dvh`），
 *         **不落** `.xg-sheet`（该类职责收窄为「设置面板底部 sheet」专用，右栏禁承接）；**无宽度手柄**；
 *         返回＝系统返回键＋面板内关闭（覆盖形态自带控件）。
 *
 * U-W1 接线（任务书 §1.3）：桌面停靠面改用 `components/RightbarHost`（push/fullscreen/手柄/拖拽/键盘，
 *   宿主 clamp）＋ `components/RightTabStrip`（tab 条：roving tabindex／键盘五路／Delete；
 *   §5 两控件搭 tab 条末端＝本件 `onToggleForm`／`onClose` 注入）。
 *   拖拽/键盘上报的新宽度在**本件** clamp（下限 `rightbar-min`、上限 视口×0.7 ∧ 列轨宽）——
 *   🔴 列轨宽唯一源仍是 `shell/columns.ts`（本批禁改）：偏好宽只在轨内收窄面板，**不扩列轨**
 *   （扩列轨＝columns 解算入参，登记为后续批的接线位，不越权先动）。
 *   折叠零代价（大王裁：右栏默认收合不得破）：collapsed ⇒ host 不渲染 body/手柄（§5 无轨条、无宽度）。
 */
import React, { useState } from "react";
import { RightbarHost } from "../components/RightbarHost";
import { RightTabStrip, type RightTabRecord } from "../components/RightTabStrip";
import { COLUMN } from "./columns";
import type { RightbarForm } from "./shellDecl";

export interface RightBarProps {
  open: boolean;
  form: RightbarForm;
  /** 无会话 ⇒ 无面板（§5） */
  canShow: boolean;
  /**
   * 手机档（<768）：**唯一形态＝fullscreen 真全屏**（★A-036；声明 `phone.rightbar_forms:[fullscreen]`）。
   * 真 ⇒ 几何走 `.xg-rightbar[data-form="fullscreen"]`（`inset:0`；手机档另加 `height:100dvh`）；
   * **不出**形态切换（切 push 在手机档无几何解）；**不落** `.xg-sheet`。
   */
  mobileFullscreen?: boolean;
  /** 覆盖形态（手机真全屏 ∨ fullscreen）：面板内须自带控件（tab 条被盖 ⇒ 声明 `overlay_controls`） */
  overlay?: boolean;
  /** 正文只读（声明 `rightbar.readonly`；右栏＝详情面，**不接上行命令**） */
  readonly?: boolean;
  /** 空闲态文案（声明 `rightbar.empty`；宿主给，缺省＝兜底常量） */
  emptyText?: string;
  onClose: () => void;
  /** 形态切换 push⇄fullscreen（承 §5：搭 tab 条末端；手机档不出） */
  onToggleForm?: () => void;
  /** 列轨宽（来自 `computeColumns` 解算；拖拽/键盘上限＝此值 ∧ 视口×0.7） */
  trackWidthPx?: number;
  /** 视口宽（clamp 上限用；宿主注入，本件零取数） */
  viewportWidth?: number;
  /** 右栏 tab 记录（宿主注入只读；§5 无标题行，tab 条即顶面） */
  tabs?: RightTabRecord[];
  /** 当前活跃 tab */
  activeTabId?: string | null;
  /** 切 tab（宿主管理 tab 态；本件零业务） */
  onFocusTab?: (tabId: string) => void;
  /** 关 tab（Delete／关闭钮；宿主管理） */
  onCloseTab?: (tabId: string) => void;
  containerRef?: React.MutableRefObject<HTMLElement | null>;
  children?: React.ReactNode;
}

/** 右栏停靠面（折叠＝`hidden`＋host 不渲染 body/手柄 ⇒ **零代价**：无轨条、无宽度） */
export function RightBar(props: RightBarProps): React.ReactElement {
  const { open, form, canShow } = props;
  const visible = open && canShow;
  const [prefWidth, setPrefWidth] = useState(0);
  /** 手机档：唯一形态＝fullscreen（真全屏；A-036）——不出形态切换、不落 `.xg-sheet` */
  const singleForm = props.mobileFullscreen === true;
  /** 覆盖形态：手机真全屏 ∨ 桌面/平板 fullscreen（两条控件此时搭**面板内**，tab 条已被盖住） */
  const overlay = props.overlay ?? (singleForm || form === "fullscreen");
  const readonly = props.readonly !== false;
  /** 覆盖形态面板内控件（声明 `rightbar.overlay_controls`：collapse ＋ form_toggle；
   *  手机档只有 fullscreen 一种形态 ⇒ 只留 collapse）。 */
  const overlayControls = overlay ? (
    <div className="xg-dock-head" data-testid="rightbar-overlay-controls">
      <button
        type="button"
        className="xg-btn"
        data-testid="rightbar-collapse"
        aria-label={singleForm ? "关闭详情面" : "折叠右栏"}
        onClick={props.onClose}
      >关闭</button>
      {singleForm ? null : (
        <button
          type="button"
          className="xg-btn"
          data-testid="rightbar-form-toggle"
          aria-label="切回挤占形态"
          aria-pressed={form === "fullscreen"}
          onClick={props.onToggleForm}
        >⤡ 挤占</button>
      )}
    </div>
  ) : null;

  const clampResize = (next: number): void => {
    const track = props.trackWidthPx ?? 0;
    const cap = Math.round((props.viewportWidth ?? track) * COLUMN.rightbarMaxRatio);
    const hi = track > 0 ? Math.min(cap, track) : cap;
    const lo = Math.min(COLUMN.rightbarMin, hi);
    setPrefWidth(Math.min(Math.max(Math.round(next), lo), hi));
  };

  const tabStrip = (
    <RightTabStrip
      tabs={props.tabs ?? []}
      activeTabId={props.activeTabId ?? null}
      onFocusTab={props.onFocusTab}
      onCloseTab={props.onCloseTab}
      onTogglePresentation={singleForm ? undefined : props.onToggleForm}
      onToggleCollapse={props.onClose}
    />
  );

  const track = props.trackWidthPx ?? 0;
  const panelWidth = prefWidth > 0 && track > 0 ? Math.min(prefWidth, track) : undefined;

  return (
    <aside
      id="seat-rightbar"
      data-testid="seat-rightbar"
      data-seat="rightbar"
      data-form={form}
      data-mobile-fullscreen={singleForm ? "true" : "false"}
      data-open={visible ? "true" : "false"}
      hidden={!visible}
      ref={props.containerRef as React.RefObject<HTMLElement> | undefined}
      className="xg-rightbar"
      aria-label="详情面"
      role={overlay ? "dialog" : undefined}
      /* 真带遮罩的手机档 ⇒ `aria-modal=true`；桌面/平板 fullscreen 无遮罩、不做焦点囚笼 ⇒ `false` */
      aria-modal={overlay ? (singleForm ? "true" : "false") : undefined}
      style={panelWidth ? { width: panelWidth } : undefined}
    >
      {/* U-W1：停靠面宿主（手柄＝仅 push 展开态；折叠零代价＝collapsed ⇒ body/handle 均不渲染） */}
      <RightbarHost
        collapsed={!visible}
        presentation={form}
        width={prefWidth > 0 ? prefWidth : track}
        canShow={canShow}
        readonly={readonly}
        /* 手机档＝真全屏单形态：不出 tab 条（形态切换控件无处可切；正文仍由 body 承载当前 tab） */
        tabStrip={visible && !singleForm ? tabStrip : null}
        tabBody={visible ? (
          <div className="xg-rightbar-content">
            {overlayControls}
            {props.children}
          </div>
        ) : null}
        onToggleCollapse={props.onClose}
        onTogglePresentation={singleForm ? undefined : props.onToggleForm}
        onResize={clampResize}
      />
    </aside>
  );
}
